import React, { useState } from "react";

import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";

import { ApplicationEndpoints } from "@generated/application";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { Application, PipelineMode } from "src/domains/application/types";

// ── Pipeline Mode Selector ─────────────────────────────────────────────────────

export const PIPELINE_MODE_OPTIONS: {
  value: PipelineMode;
  label: string;
  description: string;
  infraNote?: string;
}[] = [
  {
    value: PipelineMode.RUSHA_MANAGED,
    label: "Rusha Managed",
    description: "Rusha builds and deploys your stack automatically.",
    infraNote: "K8s deployments use GitOps (ArgoCD/Flux).",
  },
  {
    value: PipelineMode.CI_BUILD_RUSHA_DEPLOY,
    label: "CI Build → Rusha Deploy",
    description:
      "Your CI pipeline builds images. After CI completes, it calls the deploy-callback endpoint to trigger Rusha deploy.",
    infraNote: "K8s deployments use GitOps (ArgoCD/Flux).",
  },
];

interface PipelineModeSelectorProps {
  system: Application;
}

export function PipelineModeSelector({
  system,
}: PipelineModeSelectorProps): React.ReactElement {
  const [mode, setMode] = useState<string>(
    system.pipelineMode ?? PipelineMode.RUSHA_MANAGED,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = async (newMode: string) => {
    if (newMode === mode) return;
    setSaving(true);
    setError(null);
    try {
      const baseUrl = apiBaseUrls.BACKEND;
      await axios.patch(
        `${baseUrl}${ApplicationEndpoints.UPDATE_APPLICATION.replace(":id", system.alias)}`,
        { pipelineMode: newMode },
        {
          withCredentials: true,
        },
      );
      setMode(newMode);
    } catch {
      setError("Failed to update deploy mode.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 1,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <RocketLaunchIcon fontSize="small" color="action" />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Pipeline Mode</InputLabel>
          <Select
            value={mode}
            label="Pipeline Mode"
            onChange={(e) => void handleChange(e.target.value)}
            disabled={saving}
          >
            {PIPELINE_MODE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {saving && <CircularProgress size={16} />}
        {error && (
          <Typography variant="caption" color="error.main">
            {error}
          </Typography>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {PIPELINE_MODE_OPTIONS.find((o) => o.value === mode)?.description}
      </Typography>
      {PIPELINE_MODE_OPTIONS.find((o) => o.value === mode)?.infraNote && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
        >
          {PIPELINE_MODE_OPTIONS.find((o) => o.value === mode)?.infraNote}
        </Typography>
      )}
    </Box>
  );
}

export default PipelineModeSelector;
